<template>
  <div 
    v-mm-spinner="{
      visible: loading.visible,
      text: loading.text,
    }"
    class="xm-invoice-detail"
  >
    <h2 class="invoice-detail-title">
      {{ $t('invoice.invoiceDetail') }}
    </h2>
    <div
      v-if="isShowNeedRedInvoice"
      class="xm-invoice-detail-content"
    >
      <h3>{{ $t('invoice.otherOrderRedInvoice') }}</h3>
      <mm-alert
        type="warning"
        :closable="false"
      >
        <template #title>
          <span>{{ $t('invoice.pendingRedInvoiceWarning') }}
            <a
              v-for="(item, index) in needRedInvoiceList"
              :key="item"
              class="need-red-invoice-link"
              @click="handleViewDetail(item)"
            >{{ `${index > 0 ? '、' : ''}${item}` }}</a>
          </span>
        </template>
      </mm-alert>
    </div>
    <div
      v-if="referInfo.length > 0"
      class="xm-invoice-detail-content"
    >
      <h3>{{ $t('invoice.historyOrdersInvolved') }}</h3>
      <invoice-tips
        :etax-url="etaxUrl"
        :is-history="true"
      />
      <invoice-list-table
        :columns="historyInvoiceColumns"
        :list-data="referInfo"
      >
        <template #invoice_type="{ row }">
          <div
            v-for="(item, index) in row.invoice_list"
            :key="index"
            :style="{ color: item.bill_type === '2' ? '#f0150f' : 'inherit' }"
          >
            <mm-select
              v-if="shouldShowInvoiceTypeSelect(item) && !isOversea"
              v-model="item.invoice_type"
              borderless
              size="small"
              :options="INVOICE_TYPE_OPTIONS"
            />
            <span
              v-else
              style="padding-left: 8px;"
            >{{ item.invoice_type_str || '-' }}</span>
          </div>
        </template>
        <template #operation="{ row }">
          <invoice-operator
            :row-data="row"
            @operate="handleOperate"
          />
        </template>
      </invoice-list-table>
    </div>
    <div class="xm-invoice-detail-content">
      <h3>{{ $t('invoice.currentInvoiceInfo') }}</h3>
      <div class="invoice-info-item">
        <span class="invoice-info-item-label">{{ $t('invoice.invoiceTitle') }}</span>
        <span class="invoice-info-item-value">{{ applyInfo.company_name }}</span>
      </div>
      <div class="invoice-info-item">
        <span class="invoice-info-item-label">{{ $t('invoice.invoiceTaxNo') }}</span>
        <span class="invoice-info-item-value">{{ applyInfo.taxpayer_id || '-' }}</span>
      </div>
      <div class="invoice-info-table">
        <mm-table
          :columns="invoiceColumns"
          :data-source="[current]"
          :min-col-width="110"
        >
          <template
            slot="order_no"
            slot-scope="{ row }"
          >
            <a
              class="order-no-link"
              @click="handleViewOrderDetail(row.order_id)"
            >{{ row.order_no }}</a>
          </template>

          <template
            slot="product_names"
            slot-scope="{ value }"
          >
            <span>{{ renderProductNames(value) }}</span>
          </template>

          <template
            slot="transfer_in_amount"
            slot-scope="{ value }"
          >
            <span>￥{{ value || 0 }}</span>
          </template>

          <template
            slot="supplement_amount"
            slot-scope="{ value }"
          >
            <span>￥{{ value || 0 }}</span>
          </template>

          <template
            slot="amount"
            slot-scope="{ value }"
          >
            <span>￥{{ value || 0 }}</span>
          </template>

          <template
            slot="refund_count"
            slot-scope="{ value, row }"
          >
            <span :style="{ color: hasRedInvoice(row.invoice_list) ? '#f0150f' : 'inherit' }">
              ￥{{ value || 0 }}
            </span>
          </template>

          <template
            slot="invoice_type"
            slot-scope="{ row }"
          >
            <div
              v-for="(item, index) in row.invoice_list"
              :key="index"
              :style="{ color: item.bill_type === '2' ? '#f0150f' : 'inherit' }"
            >
              <template v-if="shouldShowInvoiceTypeSelect(item)">
                <mm-select
                  v-if="!isOversea"
                  v-model="item.invoice_type"
                  borderless
                  size="small"
                  :options="INVOICE_TYPE_OPTIONS"
                />
                <span v-else>{{ $t('invoice.formal') }}</span>
              </template>
              <span
                v-else
                style="padding-left: 8px;"
              >{{ item.invoice_type_str || '-' }}</span>
            </div>
          </template>

          <template
            slot="invoice_no"
            slot-scope="{ row }"
          >
            <invoice-list-field
              :invoice-list="row.invoice_list"
              field-key="number"
            />
          </template>

          <template
            slot="invoice_amount"
            slot-scope="{ row }"
          >
            <invoice-list-field
              :invoice-list="row.invoice_list"
              field-key="amount"
            />
          </template>

          <template
            slot="invoice_date"
            slot-scope="{ row }"
          >
            <invoice-list-field
              :invoice-list="row.invoice_list"
              field-key="open_invoice_time"
            />
          </template>

          <template
            slot="bill_type"
            slot-scope="{ row }"
          >
            <invoice-list-field
              :invoice-list="row.invoice_list"
              field-key="bill_type_str"
            />
          </template>

          <template
            slot="status"
            slot-scope="{ row }"
          >
            <invoice-list-field
              :invoice-list="row.invoice_list"
              field-key="status_str"
            />
          </template>

          <template
            slot="operation"
            slot-scope="{ row }"
          >
            <invoice-operator
              :row-data="row"
              @operate="handleOperate"
            />
          </template>
        </mm-table>
      </div>

      <invoice-tips
        v-if="showRedProcessTips"
        :etax-url="etaxUrl"
      />
      <div
        v-if="showRefoundTips"
        class="tips"
      >
        {{ $t('invoice.refundTransferTips') }}
      </div>
    </div>
    <change-bill-modal
      v-if="changeBillModal.visible"
      :visible="changeBillModal.visible"
      :order-id="changeBillModal.orderId"
      :invoice-item="changeBillModal.invoiceItem"
      @close="handleCloseChangeBillModal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import InvoiceApi from '@api/invoice'
import InvoiceListTable from './components/InvoiceListTable.vue'
import InvoiceListField from './components/InvoiceListField.vue'
import InvoiceOperator from './components/InvoiceOperator.vue'
import InvoiceTips from './components/InvoiceTips.vue'
import { Dialog } from 'amumu-ui'
import { INVOICE_LIST_COLUMNS, INVOICE_DETAIL_COLUMNS, INVOICE_OPERATES, INVOICE_TYPE_OPTIONS, INVOICE_STATUS, INVOICE_TYPES } from './constants'
import ChangeBillModal from './components/ChangeBillModal.vue'

export default {
  name: 'InvoiceDetail',
  components: {
    InvoiceListTable,
    InvoiceListField,
    InvoiceOperator,
    InvoiceTips,
    ChangeBillModal,
  },
  data() {
    return {
      historyInvoiceColumns: INVOICE_LIST_COLUMNS,
      invoiceColumns: INVOICE_DETAIL_COLUMNS,
      current: [],
      referInfo: [],
      orderNo: '',
      needRedInvoiceList: [],
      etaxUrl: '',
      applyInfo: {
        company_name: '',
        taxpayer_id: '',
      },
      INVOICE_TYPE_OPTIONS,
      loading: {
        visible: false,
        text: '',
      },
      INVOICE_STATUS,
      INVOICE_TYPES,
      changeBillModal: {
        visible: false,
        orderId: '',
        invoiceItem: {},
      },
    }
  },
  created() {
    const { params } = this.$route
    this.orderNo = params.orderNo
    this.loading.text = this.$t('invoice.loading')
    this.fetchInvoiceDetail()
  },
  watch: {
    // 监听路由参数变化，当orderNo改变时重新获取数据
    '$route.params.orderNo': {
      handler(newOrderNo) {
        if (!newOrderNo || newOrderNo === this.orderNo) return

        this.orderNo = newOrderNo
        this.fetchInvoiceDetail()
      },
      immediate: false
    }
  },
  computed: {
    inputDisabled() {
      return !!this.applyInfo.taxpayer_id
    },
    isShowNeedRedInvoice() {
      return this.needRedInvoiceList.length > 0
    },
    // 1.正常到款 2.有退款/转出 3. 待处理、待确认状态
    showRedProcessTips() {
      if(!this.current || !this.current.invoice_list) return false

      const enableStatus = [INVOICE_STATUS.PENDING_PROCESS, INVOICE_STATUS.PENDING_CONFIRMATION]
      const hasEnableStatus = this.current.invoice_list.some(item => enableStatus.includes(String(item.status)))
      return this.current.refund_count > 0 && hasEnableStatus
    },
    // 1.正常到款 2.有退款/转出 3. 待申请、已拒绝
    showRefoundTips() {
      if(!this.current || !this.current.invoice_list) return false

      const enableStatus = [INVOICE_STATUS.PENDING_APPLY, INVOICE_STATUS.REJECTED]
      const hasEnableStatus = this.current.invoice_list.some(item => enableStatus.includes(String(item.status)))
      return this.current.refund_count > 0 && hasEnableStatus
    },
    ...mapGetters("main", [
        "isOversea",
      ]),
  },
  methods: {
    async fetchInvoiceDetail() {
      this.loading.visible = true
      this.loading.text = this.$t('invoice.loading')

      try {
        const data = await InvoiceApi.fetchInvoiceDetail({ order_no: this.orderNo })

        if(!data || !Object.keys(data).length) return

        this.current = data.current
        this.referInfo = data.refer_info
        this.etaxUrl = data.etax_url
        this.applyInfo = data.apply_info
        this.needRedInvoiceList = data.need_red.filter(item => String(item) !== this.orderNo)
      } finally {
        this.loading.visible = false
      }
    },

    handleViewDetail(orderNo) {
      this.$router.push(`/invoice/detail/${orderNo}`)
    },

    handleViewOrderDetail(orderId) {
      this.$router.push(`/order/detail/${orderId}`)
    },

    renderProductNames(productNames) {
      if (!productNames || productNames.length === 0) return '-'
      return productNames.join('，')
    },

    hasRedInvoice(invoiceList) {
      if (!invoiceList || invoiceList.length === 0) return false
      return invoiceList.some(item => item.bill_type === '2')
    },

    // 判断是否应该显示发票类型下拉选择
    shouldShowInvoiceTypeSelect(invoiceItem) {
      return [INVOICE_STATUS.PENDING_APPLY, INVOICE_STATUS.REJECTED].includes(String(invoiceItem.status))
    },

    // 处理操作
    handleOperate({ status, invoiceItem, rowData }) {
      switch (status) {
        case INVOICE_OPERATES.APPLY:
          // 申请开票
          this.applyInvoice(invoiceItem, rowData)
          break
        case INVOICE_OPERATES.TO_CONFIRM:
          // 去电子税务局确认
          this.toConfirmInvoice()
          break
        case INVOICE_OPERATES.RED_CONFIRM:
          // 我已确认
          this.confirmInvoiceRed(invoiceItem)
          break
        case INVOICE_OPERATES.RED_PROCESS:
          // 红冲处理
          this.processInvoiceRed(invoiceItem)
          break
        case INVOICE_OPERATES.DOWNLOAD:
          // 下载发票
          this.downloadInvoice(invoiceItem, rowData)
          break
        case INVOICE_OPERATES.VIEW_DETAIL:
          // 查看详情
          this.handleViewDetail(rowData.order_no)
          break
        case INVOICE_OPERATES.REOPEN:
          // 重开申请
          this.applyReopen(invoiceItem,rowData)
          break
        case INVOICE_OPERATES.CANCEL_REOPEN:
          // 取消重开
          this.cancelReopen(invoiceItem)
          break
      }
    },
    toConfirmInvoice() {
      window.open(this.etaxUrl, '_blank')
    },
    async applyInvoice(invoiceItem, row) {
      const invoiceType = invoiceItem.invoice_type
      
      if(!invoiceType && !this.isOversea) {
        this.$message.warning(this.$t('invoice.selectInvoiceType'))
        return
      }

      try {
        await InvoiceApi.applyInvoice({
          order_id: row.order_id,
          invoice_type: this.isOversea ? INVOICE_TYPES.FORMAL : invoiceType,
        })
      } catch (err) {
        // 申请失败
      } finally {
        this.fetchInvoiceDetail()
      }

    },
    async confirmInvoiceRed(invoiceItem) {
      this.loading.visible = true
      this.loading.text = this.$t('invoice.confirming')

      try {
        await InvoiceApi.confirmInvoiceRed({
          item_id: invoiceItem.id,
        })
      } catch (err) {
        // 确认失败
      } finally {
        this.fetchInvoiceDetail()
      }
    },
    async processInvoiceRed(invoiceItem) {
      this.loading.visible = true
      this.loading.text = this.$t('invoice.redProcessing')

      try {
        await InvoiceApi.processInvoiceRed({
          item_id: invoiceItem.id,
        })
      } catch (err) {
        // 红冲处理失败
      } finally {
        this.fetchInvoiceDetail()
      }
    },
    async downloadInvoice(invoiceItem, rowData) {
      const { invoice_url, invoice_type } = invoiceItem

      // 形式发票需要调接口下载 
      if(String(invoice_type) === INVOICE_TYPES.FORMAL) {
        InvoiceApi.downloadFormalInvoice({ order_id: rowData.order_id })

        return
      }

      if(!invoice_url) {
        this.$message.warning(this.$t('invoice.downloadAddressNotExist'))
        return
      }

      window.open(invoice_url, '_blank')
    },
    handleCloseChangeBillModal() {
      this.changeBillModal.visible = false
      this.changeBillModal.orderId = ''
      this.changeBillModal.invoiceItem = {}
      this.fetchInvoiceDetail()
    },
    applyReopen(invoiceItem,row) {
      this.changeBillModal.orderId = row.order_id
      this.changeBillModal.visible = true
      this.changeBillModal.invoiceItem = invoiceItem
    },

    cancelReopen(invoiceItem) {
      Dialog.confirm({
        title: '撤回重开',
        message: '确定要撤回重开吗？',
      }).then(async () => {
        await InvoiceApi.cancelReopen({
          item_id: invoiceItem.id,
        })

        this.$message.success('撤回成功')
        this.fetchInvoiceDetail()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.xm-invoice-detail {
  padding: 20px;

  .invoice-detail-title {
    margin-bottom: 20px;
    font-size: 20px;
    color: #333;
  }

  .order-no-link {
    cursor: pointer;
  }

  .need-red-invoice-link {
    cursor: pointer;
  }

  :deep(.mm-spinner-text) {
    width: 200px;
    text-align: center;
    color: #1890ff !important;
  }

  :deep(.mm-tfixed__right .mm-tbody table) {
    min-width: 205px;
  }
}

.xm-invoice-detail-content {
  margin-bottom: 24px;

  h3 {
    margin-bottom: 12px;
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  .tips {
    margin-top: 8px;
    padding: 8px 12px;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    margin-bottom: 16px;
    line-height: 1.6;
    color: #666;
    font-size: 14px;
  }

  .tips-link {
    margin-top: 8px;

    a {
      cursor: pointer;
    }
  }

  .invoice-info-item {
    display: flex;
    align-items: center;

    &-label {
      flex-shrink: 0;
    }

    &-input {
      width: 240px;
    }

    & + .invoice-info-item {
      margin-top: 8px;
    }
  }

  .invoice-info-table {
    margin-top: 20px;
  }
}

</style>